import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getHomeData, clearHomeCache } from '../../Public/home-data';
import { getLandingData } from '../../Public/landing-data';
import LazyOnView from '../../Utils/LazyLoading/LazyOnView';
import { fillSeoTokens } from '../../Services/UtilsService';

import HomeHeader from './Sections/home-header-component';
import Hero from './Sections/hero-component';
import WhatWeFix from './Sections/what-we-fix-component';
import WhyChooseUs from './Sections/why-choose-us-component';
import Process from './Sections/process-component';
import Services from './Sections/services-component';
import Audience from './Sections/audience-component';
import { getAudienceData } from '../../Public/audience-data';
import ServiceArea from './Sections/service-area-component';
import FreeReviewForm from './Sections/free-review-form-component';
import LearningCenter from './Sections/learning-center-component';
import Faq from './Sections/faq-component';
import ClosingCta from './Sections/closing-cta-component';
import HomeFooter from './Sections/home-footer-component';

export default function HomeScreen() {
    const { language = 'en', dir = 'ltr', businessInfo = {} } = useSelector((s) => s.systemData || {});
    const [home, setHome] = useState(null);
    const [loading, setLoading] = useState(true);

    // This is the standalone home (superseded by Landing at "/"). Like Landing,
    // it uses the shared landing SEO; it just sets document.title directly since
    // it doesn't wrap in <Layout>.
    useEffect(() => {
        const { seo } = getLandingData(language);
        document.title = fillSeoTokens(seo.title, {
            businessName: businessInfo.businessName,
            website: businessInfo.website,
        });
    }, [language, businessInfo.businessName, businessInfo.website]);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        clearHomeCache();
        getHomeData().then((data) => {
            if (!alive) return;
            setHome(data);
            setLoading(false);
        });
        return () => { alive = false; };
    }, [language]);

    if (loading || !home) {
        return (
            <div dir={dir} className="min-h-screen bg-white">
                <div className="mx-auto max-w-[1200px] px-6 py-24">
                    <div className="h-10 w-2/3 animate-pulse rounded bg-surface-alt" />
                    <div className="mt-4 h-6 w-1/2 animate-pulse rounded bg-surface-alt" />
                    <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
                        {[0, 1, 2].map((i) => <div key={i} className="h-48 animate-pulse rounded-card bg-surface-alt" />)}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id="top" dir={dir} className="min-h-screen bg-white font-primary text-ink">
            <HomeHeader business={home.businessInfo} />

            {/* Above the fold */}
            <Hero data={home.hero} />
            <WhatWeFix data={home.whatWeFix} />

            {/* Below the fold, lazy on view */}
            <LazyOnView><WhyChooseUs data={home.whyChooseUs} /></LazyOnView>
            <LazyOnView><Process data={home.process} /></LazyOnView>
            <LazyOnView><Services data={home.services} /></LazyOnView>
            <LazyOnView><Audience data={getAudienceData(language)} /></LazyOnView>
            <LazyOnView><ServiceArea data={home.serviceArea} /></LazyOnView>
            <LazyOnView><FreeReviewForm data={home.contact} /></LazyOnView>
            <LazyOnView><LearningCenter data={home.guides} /></LazyOnView>
            <LazyOnView><Faq data={home.faq} /></LazyOnView>
            <LazyOnView><ClosingCta data={home.cta} /></LazyOnView>

            <HomeFooter data={home.footer} />
        </div>
    );
}
